import { useCallback, useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { ChevronsDownUp, ChevronsUpDown, Settings } from "lucide-react"
import { testIds } from "@/lib/test-ids"

export type WorkspaceRowMenuState = { x: number; y: number; wsId: string } | null

export function openWorkspaceOrganizationSettings(organizationId: string) {
  window.dispatchEvent(
    new CustomEvent("open-settings", {
      detail: { tab: "organizations", organizationId },
    }),
  )
}

export function useWorkspaceRowContextMenu() {
  const [menu, setMenu] = useState<WorkspaceRowMenuState>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menu) return
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenu(null)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null)
    }
    document.addEventListener("mousedown", onDown, true)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown, true)
      document.removeEventListener("keydown", onKey)
    }
  }, [menu])

  const openMenu = useCallback((wsId: string, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setMenu({ x: event.clientX, y: event.clientY, wsId })
  }, [])

  const closeMenu = useCallback(() => setMenu(null), [])

  return { menu, menuRef, openMenu, closeMenu }
}

type WorkspaceRowContextMenuPortalProps = {
  menu: WorkspaceRowMenuState
  menuRef: React.RefObject<HTMLDivElement | null>
  onClose: () => void
  onToggleWorkspace: (wsId: string) => void
  onHideOtherWorkspaces?: (wsId: string) => void
  activeWorkspaceIds: string[]
  /** When true, hide action is "Show workspace" (hidden from scope). */
  workspaceHiddenFromScope?: boolean
  /** Tab bar groups only. Browse rows leave this unset so the menu stays identical. */
  groupCollapse?: { collapsed: boolean; onToggle: () => void } | null
}

export function WorkspaceRowContextMenuPortal({
  menu,
  menuRef,
  onClose,
  onToggleWorkspace,
  onHideOtherWorkspaces,
  activeWorkspaceIds,
  workspaceHiddenFromScope = false,
  groupCollapse = null,
}: WorkspaceRowContextMenuPortalProps) {
  if (!menu || typeof document === "undefined") return null

  const hideOthersDisabled =
    activeWorkspaceIds.length === 1 && activeWorkspaceIds[0] === menu.wsId

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[180px] rounded-md border border-border bg-popover py-0.5 shadow-lg"
      style={{ left: menu.x, top: menu.y }}
      role="menu"
    >
      <button
        type="button"
        role="menuitem"
        className="w-full px-2.5 py-1.5 text-left text-xs hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
        data-testid={testIds.leftSidebar.workspaceRowSettings(menu.wsId)}
        onClick={() => {
          openWorkspaceOrganizationSettings(menu.wsId)
          onClose()
        }}
      >
        <Settings className="h-3.5 w-3.5 shrink-0 opacity-70" />
        Settings
      </button>
      {onHideOtherWorkspaces ? (
        <>
          <div className="my-0.5 h-px bg-border" role="separator" />
          <button
            type="button"
            role="menuitem"
            disabled={hideOthersDisabled}
            className="w-full px-2.5 py-1.5 text-left text-xs hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:pointer-events-none"
            data-testid={testIds.leftSidebar.workspaceRowHideOthers(menu.wsId)}
            onClick={() => {
              onHideOtherWorkspaces(menu.wsId)
              onClose()
            }}
          >
            Hide others
          </button>
        </>
      ) : null}
      <button
        type="button"
        role="menuitem"
        className="w-full px-2.5 py-1.5 text-left text-xs hover:bg-accent hover:text-accent-foreground"
        data-testid={
          workspaceHiddenFromScope
            ? testIds.leftSidebar.openTabsShowWorkspace(menu.wsId)
            : testIds.leftSidebar.workspaceRowHide(menu.wsId)
        }
        onClick={() => {
          onToggleWorkspace(menu.wsId)
          onClose()
        }}
      >
        {workspaceHiddenFromScope ? "Show workspace" : "Hide workspace"}
      </button>
      {groupCollapse ? (
        <>
          <div className="my-0.5 h-px bg-border" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="w-full px-2.5 py-1.5 text-left text-xs hover:bg-accent hover:text-accent-foreground flex items-center gap-2"
            data-testid={testIds.documentTabs.groupToggle(menu.wsId)}
            onClick={() => {
              groupCollapse.onToggle()
              onClose()
            }}
          >
            {groupCollapse.collapsed ? (
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
            ) : (
              <ChevronsDownUp className="h-3.5 w-3.5 shrink-0 opacity-70" />
            )}
            {groupCollapse.collapsed ? "Expand group" : "Collapse group"}
          </button>
        </>
      ) : null}
    </div>,
    document.body,
  )
}
